import toInteger from 'lodash/toInteger';
import { createCanvas, drawLayerBaseInfo } from '@/utils/file-kit';
import { getMainLayerRenderSize } from '@/utils/layer-kit';
import type { IImageLayerProps, ILayerProps, ISize } from '@/version-types';

export interface IRenderMainLayerCanvasOptions {
    layer: ILayerProps;
    baseImage: HTMLCanvasElement | HTMLImageElement;
    /** 是否将图层 opacity 烘焙进图片 canvas；edgeless 展示时应为 false，由外层 render 单独施加 */
    applyLayerOpacity?: boolean;
    /** 是否强制重新渲染 */
    forceRerender?: boolean;
}

export function renderMainLayerCanvas({
    layer,
    baseImage,
    applyLayerOpacity = false,
    forceRerender = false,
}: IRenderMainLayerCanvasOptions) {
    const { opacity } = layer;
    const data = layer.data as IImageLayerProps;
    const {
        naturalWidth,
        naturalHeight,
        imageTransform: { flipH, flipV },
    } = data;
    const originImgWidth = Math.max(1, toInteger(naturalWidth));
    const originImgHeight = Math.max(1, toInteger(naturalHeight));
    const { width, height } = getMainLayerRenderSize(layer);

    if (!forceRerender) {
        // 无翻转、无需烘焙透明度、且尺寸一致时，直接返回原图跳过 canvas 绘制
        const noFlip = !flipH && !flipV;
        const noOpacity = !applyLayerOpacity || opacity === 1;
        const noResize = baseImage.width === width && baseImage.height === height;
        if (noFlip && noOpacity && noResize) {
            if (baseImage instanceof HTMLImageElement) {
                return createCanvas(baseImage.width, baseImage.height, baseImage).canvas;
            }
            return baseImage;
        }
    }

    const { canvas: baseCanvas, ctx: baseCtx } = drawLayerBaseInfo(
        originImgWidth,
        originImgHeight,
        flipH,
        flipV,
        applyLayerOpacity ? opacity : 1,
    );
    baseCtx.drawImage(baseImage, 0, 0, baseImage.width, baseImage.height, 0, 0, originImgWidth, originImgHeight);

    const { canvas, ctx } = createCanvas(width, height);
    ctx.drawImage(baseCanvas, 0, 0, originImgWidth, originImgHeight, 0, 0, width, height);

    return canvas;
}

/**
 * 按主图的位置和旋转信息将图片绘制到舞台画布
 *
 * @param layer - 主图图层
 * @param layerCanvas - 已应用图片缩放和翻转的主图画布
 * @param stageSize - 舞台尺寸
 * @returns 与舞台同尺寸的主图合成画布
 */
export function renderMainLayerToStage(layer: ILayerProps, layerCanvas: HTMLCanvasElement, stageSize: ISize) {
    const { left, top, opacity, rotate } = layer;
    const layerWidth = layerCanvas.width;
    const layerHeight = layerCanvas.height;
    const { canvas, ctx } = createCanvas(stageSize.width, stageSize.height);

    ctx.translate(left + layerWidth / 2, top + layerHeight / 2);
    ctx.rotate((Math.PI / 180) * rotate);
    ctx.globalAlpha = opacity;
    ctx.drawImage(layerCanvas, -layerWidth / 2, -layerHeight / 2);

    return canvas;
}
